/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function getOpenOrderTotals(orderReference) {
  let regularQty = 0;
  let campaignQty = 0;
  for (const order of orderReference) {
    if (order.isCampaign) {
      campaignQty += order.quantity || 0;
    } else {
      regularQty += order.quantity || 0;
    }
  }
  return { regularQty, campaignQty };
}

document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('ir:stock-references:fetch', () => {
    renderProductInventory();
  });
  window._inventoryCache = window._inventoryCache || {};

  const upsertInventoryTooltip = (target, inventoryItem) => {
    if (!target) return;
    let tooltip = target.querySelector('.inventory-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'inventory-tooltip';
      target.appendChild(tooltip);
    }
    tooltip.innerHTML = '';
    const nextArrivalDate = window.NextDeliveryDate?.getVisibleNextDeliveryDate(
      inventoryItem.nextArrivalDate
    ) || '';
    if (nextArrivalDate) {
      tooltip.innerHTML = `<div><strong>${window.theme.strings.irStockStatus.nextDeliveryLabel}:</strong> ${nextArrivalDate}</div>`;
    }
    tooltip.innerHTML += `<div><strong>${window.theme.strings.irStockStatus.irAvailableStockLabel}:</strong> ${inventoryItem.dsvAvailableStock} / <strong>${window.theme.strings.irStockStatus.irTotalStockLabel}:</strong> ${inventoryItem.dsvTotalStock}</div>
    <div><strong>${window.theme.strings.irStockStatus.storeIncomingStockLabel}:</strong> ${inventoryItem.incomingStock} / <strong>${window.theme.strings.irStockStatus.storeAvailableStockLabel}:</strong> ${inventoryItem.avalableStock} / <strong>${window.theme.strings.irStockStatus.storeTotalStockLabel}:</strong> ${inventoryItem.totalStock}</div>`;

    const productId = target.dataset.productId;
    if (productId && window._openOrderCache[productId]?.orderReference) {
      const { regularQty, campaignQty } = getOpenOrderTotals(window._openOrderCache[productId].orderReference);
      if (regularQty > 0 || campaignQty > 0) {
        upsertOpenOrderTooltip(tooltip, regularQty, campaignQty);
      }
    }
  };

  function applyInventoryToElements(data, container) {
    const scope = container || document;
    scope.querySelectorAll('.inventory-store-customer').forEach(item => {
      const productId = item.dataset.productId;
      if (productId && data[productId]) {
        const variantSku = item.dataset.variantSku;
        if (variantSku) {
          data[productId].inventory.forEach(inventoryItem => {
            if (inventoryItem.sku === variantSku) {
              inventoryItem.nextArrivalDate = item.dataset.nextArrivalDate || '';
              if (item.querySelector('.inventory-store-ir-value')) {
                item.querySelector('.inventory-store-ir-value').textContent = `${inventoryItem.dsvAvailableStock} / ${inventoryItem.dsvTotalStock}`;
                if (inventoryItem.dsvAvailableStock == 0) {
                  item.querySelector('.inventory-store-ir-value').classList.add('out-stock');
                } else if (inventoryItem.dsvAvailableStock < 10) {
                  item.querySelector('.inventory-store-ir-value').classList.add('low-stock');
                  item.querySelector('.inventory-store-ir-value').classList.remove('out-stock');
                } else {
                  item.querySelector('.inventory-store-ir-value').classList.add('instock');
                  item.querySelector('.inventory-store-ir-value').classList.remove('out-stock');
                }
              }
              if (item.querySelector('.inventory-store-customer-value')) {
                item.querySelector('.inventory-store-customer-value').textContent = `${inventoryItem.incomingStock} / ${inventoryItem.avalableStock} / ${inventoryItem.totalStock}`;
                if (inventoryItem.totalStock == 0) {
                  item.querySelector('.inventory-store-customer-value').classList.add('out-stock');
                } else if (inventoryItem.totalStock < 10) {
                  item.querySelector('.inventory-store-customer-value').classList.add('low-stock');
                  item.querySelector('.inventory-store-customer-value').classList.remove('out-stock');
                } else {
                  item.querySelector('.inventory-store-customer-value').classList.add('instock');
                  item.querySelector('.inventory-store-customer-value').classList.remove('out-stock');
                }
              }
              upsertInventoryTooltip(item, inventoryItem);
              item.classList.remove('hidden');
            }
          });
        }
      }
    });
  }

  window.applyInventoryFromCache = function(container) {
    if (Object.keys(window._inventoryCache).length > 0) {
      applyInventoryToElements(window._inventoryCache, container);
    }
  };

  // render open order & inventory status for products in multiple areas
  function renderProductInventory() {
    if (window.theme.customer.retailerId) {
      let options = {
        retailerId: window.theme.customer.retailerId,
        products: []
      }
      document.querySelectorAll('.inventory-store-customer').forEach(item => {
        const productId = item.dataset.productId;
        const variantSku = item.dataset.variantSku;
        if (productId && variantSku) {
          options.products.push({
            id: productId,
            variants: [
              {
                sku: variantSku
              }
            ]
          });
        }
      });

      if (options.products.length > 0) {
        fetch('/apps/indeks-retail-dashboard/proxy/order/stock', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(options)
        })
        .then(response => response.json())
        .then(data => {
          Object.assign(window._inventoryCache, data);
          applyInventoryToElements(data);
        });
        
      }
    }
  }

  window._openOrderCache = window._openOrderCache || {};

  function renderOpenOrderSummary() {
    if (!window.theme.customer.retailerId) return;

    const items = document.querySelectorAll('.inventory-store-customer:not(op-product-stock-status .inventory-store-customer)');
    const productsMap = {};
    for (const item of items) {
      const productId = item.dataset.productId;
      const variantSku = item.dataset.variantSku;
      if (productId && variantSku) {
        if (!productsMap[productId]) {
          productsMap[productId] = { id: productId, variants: [] };
        }
        const skus = productsMap[productId].variants.map(v => v.sku);
        if (!skus.includes(variantSku)) {
          productsMap[productId].variants.push({ sku: variantSku });
        }
      }
    }

    const products = Object.values(productsMap);
    if (!products.length) return;

    const options = {
      retailerId: window.theme.customer.retailerId,
      products
    };

    fetch('/apps/indeks-retail-dashboard/proxy/order/reference', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options)
    })
    .then(response => response.json())
    .then(data => {
      Object.assign(window._openOrderCache, data);
      applyOpenOrderSummary(
        data,
        document.querySelectorAll('.inventory-store-customer:not(op-product-stock-status .inventory-store-customer)')
      );
    })
    .catch(error => console.error(error));
  }

  function applyOpenOrderSummary(data, items) {
    const strings = window.theme.strings.opOpenOrders;
    for (const item of items) {
      const productId = item.dataset.productId;

      const card = item.closest('.card--product');
      const customDataWrapper = card?.querySelector('.custom-data-wrapper');
      const existingInline = card?.querySelector('.open-order-summary');
      if (existingInline) existingInline.remove();

      if (!productId || !data[productId]?.orderReference) {
        item.querySelector('.open-order-summary')?.classList.add('hidden');
        continue;
      }

      const { regularQty, campaignQty } = getOpenOrderTotals(data[productId].orderReference);
      if (regularQty === 0 && campaignQty === 0) {
        item.querySelector('.open-order-summary')?.classList.add('hidden');
        continue;
      }

      if (customDataWrapper) {
        const summary = document.createElement('div');
        summary.className = 'open-order-summary flex flex-wrap gap-1';
        summary.className += ' text-sm mt-2';
        summary.innerHTML = `<b>${strings.openOrderLabel}:</b> <span>${strings.regularLabel} ${regularQty} / ${strings.campaignLabel}: ${campaignQty}</span>`;
        customDataWrapper.parentNode.insertBefore(summary, customDataWrapper);
      } else {
        let summary = item.querySelector('.open-order-summary');
        if (!summary) {
          summary = document.createElement('div');
          item.insertBefore(summary, item.firstChild);
        }
        summary.className = 'open-order-summary flex flex-wrap gap-1';
        summary.innerHTML = `<b>${strings.openOrderLabel}:</b> <span>${regularQty} / ${campaignQty}</span>`;
      }

      const tooltip = item.querySelector('.inventory-tooltip');
      if (tooltip) {
        upsertOpenOrderTooltip(tooltip, regularQty, campaignQty);
      }
    }
  }

  function upsertOpenOrderTooltip(tooltip, regularQty, campaignQty) {
    const strings = window.theme.strings.opOpenOrders;
    let openOrderRow = tooltip.querySelector('.inventory-tooltip__open-order');
    if (!openOrderRow) {
      openOrderRow = document.createElement('div');
      openOrderRow.className = 'inventory-tooltip__open-order';
      tooltip.insertBefore(openOrderRow, tooltip.firstChild);
    }
    openOrderRow.innerHTML = `<strong>${strings.openOrderLabel}:</strong> ${strings.regularLabel} ${regularQty} / ${strings.campaignLabel}: ${campaignQty}`;
  }

  window.applyOpenOrderFromCache = function(container) {
    if (Object.keys(window._openOrderCache).length === 0) return;

    const scope = container || document;
    const items = scope.querySelectorAll('.inventory-store-customer:not(op-product-stock-status .inventory-store-customer)');
    applyOpenOrderSummary(window._openOrderCache, items);
  };

  renderProductInventory();
  renderOpenOrderSummary();
  document.addEventListener('on:facet-filters:updated', (event) => {
    renderProductInventory();
    renderOpenOrderSummary();
  });
  document.addEventListener('ir:stock-references:fetch', () => {
    renderOpenOrderSummary();
  });

  function renderProductStandardPrice() {
    let skus = [];
    document.querySelectorAll('.catalog-price-table').forEach(table => {
      const sku = table.dataset.sku;
      if (sku) {
        skus.push(sku);
      }
    });
    if (skus.length > 0 && window.theme.customer.retailerId) {
      skus = [...new Set(skus)];
      fetch(`/apps/ax/order-portal/retailers/${window.theme.customer.retailerId}/standard_prices?skus=${skus.join(',')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(data => {
        skus.forEach(sku => {
          if (data[sku]) {
            const tbodies = document.querySelectorAll(`[data-sku="${sku}"] tbody`);
            if (tbodies.length) {
              tbodies.forEach(tbody => {
                let unit = tbody.closest('.catalog-price-table')?.dataset.unit;
                let originalHtml = tbody.innerHTML;
                let priceHtml = '';
                let price = Shopify.formatMoney(parseFloat(data[sku].price?.amount || 0).toFixed(2) * 100);
                priceHtml += `<tr>
                    <td class="text-theme-light">V/${data[sku].quantityRule?.minimum || 1} ${window.theme.strings.opPrice.pcs}</td>
                    <td class="font-bold text-end">${price}${unit ? ` /${unit}` : ''}</td>
                    ${tbody.closest('.catalog-price-table-3-columns') ? `<td class="font-bold">0%</td>` : ''}</tr>`;
                if (data[sku].quantityPriceBreaks.length) {
                  data[sku].quantityPriceBreaks.forEach(priceBreak => {
                    let price2 = Shopify.formatMoney(parseFloat(priceBreak.price?.amount).toFixed(2) * 100);
                    let priceDiff = data[sku].price?.amount - priceBreak.price?.amount;
                    let discountPercent = priceDiff ? parseFloat(priceDiff.toFixed(2)) / data[sku].price?.amount * 100 : 0;
                    priceHtml += `<tr>
                      <td class="text-theme-light">V/${priceBreak.minimumQuantity} ${window.theme.strings.opPrice.pcs}</td>
                      <td class="font-bold text-end">${price2}${unit ? ` /${unit}` : ''}</td>
                      ${tbody.closest('.catalog-price-table-3-columns') ? `<td class="font-bold">${Math.floor(discountPercent)}%</td>` : ''}</tr>`;
                  });
                }
                if (priceHtml && normalizeHtml(priceHtml) != normalizeHtml(originalHtml)) {
                  if (tbody.closest('.catalog-price-table-3-columns')) {
                    tbody.innerHTML = priceHtml + `<tr><td colspan="3" class="font-bold">${window.theme.strings.opPrice.campaignPrice}</td></tr>` + tbody.innerHTML;
                  } else {
                    tbody.innerHTML = priceHtml + `<tr><td colspan="2" class="font-bold"><br/>${window.theme.strings.opPrice.campaignPrice}</td></tr>` + tbody.innerHTML;
                  }
                }
              })
            }
          }
        });
      });
    }
  }

  renderProductStandardPrice();

  function normalizeHtml(html) {
    const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, "text/html");
    const root = doc.getElementById("r");

    // remove whitespace-only text nodes
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const toRemove = [];
    while (walker.nextNode()) {
      const n = walker.currentNode;
      if (!n.nodeValue.trim()) toRemove.push(n);
      else n.nodeValue = n.nodeValue.replace(/\s+/g, " ").trim();
    }
    toRemove.forEach(n => n.parentNode.removeChild(n));

    return root.innerHTML;
  }

  // Dynamic price base on quantity on product card
  // document on change quantity
  document.addEventListener('change', (event) => {
    if (event.target.closest('.qty-input__input')) {
      const quantity = event.target.value;
      const variantId = event.target.dataset.variantId;
      if (quantity && variantId) {
        if (window.volumePrices && window.volumePrices[variantId]) {
          const priceItem = window.volumePrices[variantId]
            .sort((a, b) => b.quantity - a.quantity)
            .find(price => price.quantity <= quantity);
          let priceElements = event.target.closest('.card--product')?.querySelectorAll('.dynamic-price-for-quantity');
          if (priceItem && priceElements) {
            let newPrice = Shopify.formatMoney(priceItem.price * quantity);
            priceElements.forEach(priceElement => {
              priceElement.textContent = newPrice;
            });
          }
        }
      }
    }
  });

  document.addEventListener('op-search-input', (event) => {
    let searchTerm = event.detail.searchTerm;
    let dateType = event.detail.dateType;
    let dateFrom = event.detail.dateFrom;
    let dateTo = event.detail.dateTo;
    // console.log(event.detail);
    let params = '';
    if (searchTerm && searchTerm.length >= 3) {
      params = 'query=' + searchTerm;
    }
    if (dateType == 'before_date' && dateTo) {
      if (params != '') {
        params += '&';
      }
      params += `order_date_to=${dateTo}`;
    } else if (dateType == 'after_date' && dateFrom) {
      if (params != '') {
        params += '&';
      }
      params += `order_date_from=${dateFrom}`;
    } else if (dateType == 'between_dates' && dateFrom && dateTo) {
      if (params != '') {
        params += '&';
      }
      params += `order_date_from=${dateFrom}&order_date_to=${dateTo}`;
    }
    let predictiveSearch = document.querySelector('predictive-search');
    if (!predictiveSearch) return;

    // Start a new request generation: bumps the id and aborts any in-flight
    // search so a stale, slower response can no longer overwrite this one.
    const { id: requestId, signal } = opSearchRequest.newRequest();

    // When the user is not logged in or is not linked to a retailer/company,
    // fall back to a products-only predictive search using Shopify's native
    // endpoint, rendered into the same "products" tab layout used for
    // retailer customers. Orders/invoices/campaigns are retailer-only.
    if (!window.theme.customer.retailerId) {
      if (searchTerm && searchTerm.length >= 2) {
        fetchStandardProductSearch(predictiveSearch, searchTerm, requestId, signal);
      }
      return;
    }

    if (!params) return;

    predictiveSearch.setAttribute('loading', '');
    predictiveSearch.setAttribute('results', true);
    predictiveSearch.open();
    fetch(`/apps/ax/order-portal/retailers/${window.theme.customer.retailerId}/search?${params}`, { signal })
        .then(response => response.json())
        .then(data => {
          // Ignore a response that has been superseded by a newer search.
          if (opSearchRequest.isStale(requestId)) return;
          if (data.products.length || data.orders.length || data.invoices.length || data.campaigns.length) {
            let productResults = '';
            let productHandles = [];
            const quickAddProducts = [];
            let isFirst = false;
            predictiveSearch.querySelectorAll('.tablist__tab').forEach(tab => {
              tab.setAttribute('aria-selected', 'false');
            });
            predictiveSearch.querySelectorAll('.predictive-search__tabpanel').forEach(panel => {
              panel.setAttribute('hidden', 'true');
            });
            for (let i = 0; i < data.products.length; i++) {
              if (data.products[i].handle) {
                productHandles.push(data.products[i].handle);
                let priceHtml = '';
                let displayPrice = '';
                if (data.products[i].priceModel?.price?.amount) {
                  let priceAmount = parseFloat(data.products[i].priceModel.price.amount).toFixed(2);
                  priceHtml = Shopify.formatMoney(priceAmount * 100);
                  displayPrice = priceAmount;
                  if (data.products[i].priceModel.quantityPriceBreaks?.length) {
                    if (data.products[i].priceModel.quantityPriceBreaks[0].price?.amount) {
                      let smallestPrice = parseFloat(data.products[i].priceModel.quantityPriceBreaks[0].price?.amount).toFixed(2);
                      data.products[i].priceModel.quantityPriceBreaks.forEach(priceBreak => {
                        if (priceBreak.price?.amount) {
                          if (parseFloat(priceBreak.price.amount).toFixed(2) < smallestPrice) {
                            smallestPrice = parseFloat(priceBreak.price.amount).toFixed(2);
                          }
                        }
                      });
                      if (smallestPrice && smallestPrice < priceAmount) {
                        priceHtml = ` ${window.theme.strings.opSearch.fromPrice} ${Shopify.formatMoney(smallestPrice * 100)}`;
                        displayPrice = smallestPrice;
                      }
                    }
                  }
                  
                }
                if (window.LocalCartSearch?.enabled() && data.products[i].sku) {
                  const quickAddProduct = {
                    key: data.products[i].sku,
                    sku: data.products[i].sku,
                    handle: data.products[i].handle,
                    title: data.products[i].title,
                    image: data.products[i].featuredMediaUrl,
                    priceHtml: displayPrice > 0 ? priceHtml : ''
                  };
                  quickAddProducts.push(quickAddProduct);
                  productResults += window.LocalCartSearch.renderRow(quickAddProduct);
                  continue;
                }
                productResults += `<li class="predictive-search__item" role="option">
                  <a class="predictive-result predictive-result--suggestion block focus-inset" href="/products/${data.products[i].handle}">
                    ${data.products[i].featuredMediaUrl ? `<img src="${data.products[i].featuredMediaUrl}" alt="${data.products[i].title}" class="op-search-product-image">` : ''}
                    <span class="op-search-product-title-price inline-flex flex-wrap flex-col gap-2">
                      <span class="font-bold">${data.products[i].title}</span>
                      ${displayPrice > 0 ? `<span class="op-search-product-price" data-sku="${data.products[i].sku}">${priceHtml}</span>` : ''}
                    </span>
                  </a>
                </li>`;
              }
            }
            if (productResults) {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="products"]').innerHTML = quickAddProducts.length
                ? window.LocalCartSearch.wrapResults(productResults, quickAddProducts.length)
                : productResults;
              predictiveSearch.querySelector('.tablist__tab[data-type="products"]').classList.remove('hidden');
              if (!isFirst) {
                predictiveSearch.querySelector('.tablist__tab[data-type="products"]').setAttribute('aria-selected', 'true');
                predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="products"]').removeAttribute('hidden');
                isFirst = true;
              }
            } else {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="products"]').innerHTML = '';
              predictiveSearch.querySelector('.tablist__tab[data-type="products"]').classList.add('hidden');
            }

            let orderResults = '';
            for (let i = 0; i < data.orders.length; i++) {
              if (data.orders[i].orderId && data.orders[i].axOrderId) {
                orderResults += `<li class="predictive-search__item" role="option">
                  <a class="predictive-result predictive-result--suggestion block focus-inset" href="/pages/ordredetaljer?id=${data.orders[i].orderId}">
                    ${data.orders[i].axOrderId}
                    </a>
                  </li>`;
              }
            }
            if (orderResults) {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="order"]').innerHTML = orderResults;
              predictiveSearch.querySelector('.tablist__tab[data-type="order"]').classList.remove('hidden');
              if (!isFirst) {
                predictiveSearch.querySelector('.tablist__tab[data-type="order"]').setAttribute('aria-selected', 'true');
                predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="order"]').removeAttribute('hidden');
                isFirst = true;
              }
            } else {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="order"]').innerHTML = '';
              predictiveSearch.querySelector('.tablist__tab[data-type="order"]').classList.add('hidden');
            }

            let invoiceResults = '';
            for (let i = 0; i < data.invoices.length; i++) {
              if (data.invoices[i].invoiceId) {
                invoiceResults += `<li class="predictive-search__item" role="option">
                  <a class="predictive-result predictive-result--suggestion block focus-inset" href="/pages/faktura-detaljer?id=${data.invoices[i].invoiceId}">
                      ${data.invoices[i].invoiceId}
                    </a>
                  </li>`;
              }
            }
            if (invoiceResults) {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="invoice"]').innerHTML = invoiceResults;
              predictiveSearch.querySelector('.tablist__tab[data-type="invoice"]').classList.remove('hidden');
              if (!isFirst) {
                predictiveSearch.querySelector('.tablist__tab[data-type="invoice"]').setAttribute('aria-selected', 'true');
                predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="invoice"]').removeAttribute('hidden');
                isFirst = true;
              }
            } else {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="invoice"]').innerHTML = '';
              predictiveSearch.querySelector('.tablist__tab[data-type="invoice"]').classList.add('hidden');
            }

            let campaignResults = '';
            for (let i = 0; i < data.campaigns.length; i++) {
              if (data.campaigns[i].displayId && data.campaigns[i].name) {
                if (data.campaigns[i].shopifyOrderId) {
                  campaignResults += `<li class="predictive-search__item" role="option">
                    <a class="predictive-result predictive-result--suggestion block focus-inset" href="/pages/ordredetaljer?id=${data.campaigns[i].shopifyOrderId}">
                      <span>${data.campaigns[i].displayId} - ${data.campaigns[i].name}</span>
                    </a>
                  </li>`;
                } else {
                  campaignResults += `<li class="predictive-search__item" role="option">
                    <span class="predictive-result predictive-result--suggestion block">${data.campaigns[i].displayId} - ${data.campaigns[i].name}</span>
                  </li>`;
                }
              }
            }
            if (campaignResults) {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="campaign"]').innerHTML = campaignResults;
              predictiveSearch.querySelector('.tablist__tab[data-type="campaign"]').classList.remove('hidden');
              if (!isFirst) {
                predictiveSearch.querySelector('.tablist__tab[data-type="campaign"]').setAttribute('aria-selected', 'true');
                predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="campaign"]').removeAttribute('hidden');
                isFirst = true;
              }
            } else {
              predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="campaign"]').innerHTML = '';
              predictiveSearch.querySelector('.tablist__tab[data-type="campaign"]').classList.add('hidden');
            }

            if (productResults || orderResults || invoiceResults || campaignResults) {
              predictiveSearch.querySelector('tabbed-content').classList.remove('hidden');
              predictiveSearch.querySelector('.predictive-search__no-results').classList.add('hidden');

              if (quickAddProducts.length) {
                window.LocalCartSearch.hydrate(
                  predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="products"]'),
                  { skus: quickAddProducts.map((product) => product.sku) },
                  { signal, isStale: () => opSearchRequest.isStale(requestId) }
                );
              } else if (productResults && productHandles.length) {
                fetchProductExtraData(productHandles, requestId, signal)
              }
              predictiveSearch.querySelector('.tablist__tab[data-type]:first-child').click();
            } else {
              throw new Error('No results found');
            }
          } else {
            throw new Error('No results found');
          }
        }).catch((error) => {
          // A request aborted by a newer search, or one that has been
          // superseded, must not render anything.
          if (error?.name === 'AbortError' || opSearchRequest.isStale(requestId)) return;
          console.error(error);
          predictiveSearch.querySelector('tabbed-content').classList.add('hidden');
          let noResultsMessage = window.theme.strings.opSearch.noResults.replace('[keywords]', `"${searchTerm}"`);
          if (searchTerm.length < 3) {
            noResultsMessage = window.theme.strings.opSearch.noResultsNoKeywords;
          }
          predictiveSearch.querySelector('.predictive-search__no-results').textContent = noResultsMessage;
          predictiveSearch.querySelector('.predictive-search__no-results').classList.remove('hidden');
        })
        .finally(() => {
          if (opSearchRequest.isStale(requestId)) return;
          predictiveSearch.removeAttribute('loading');
          predictiveSearch.setAttribute('results', true);
          predictiveSearch.open();
        });
  });

  document.addEventListener('click', (event) => {
    const clearBtn = event.target.closest('a[name="clear-all-cart-items"]');
    if (clearBtn) {
      event.preventDefault();
      const popup = document.querySelector('ir-popup');
      if (!popup?.show) {
        if (!confirm(window.theme.strings.clearCartConfirmation)) return;
        fetch(window.Shopify.routes.root + 'cart/clear.js', { method: 'POST' })
          .then(response => response.json())
          .then(() => window.location.reload())
          .catch(error => console.error(error));
        return;
      }
      popup.show({
        type: 'confirm',
        title: window.theme.strings.clearCartTitle,
        message: window.theme.strings.clearCartConfirmation,
        onConfirm: () => {
          fetch(window.Shopify.routes.root + 'cart/clear.js', { method: 'POST' })
            .then(response => response.json())
            .then(() => window.location.reload())
            .catch(error => console.error(error));
        }
      }, clearBtn);
    }
  });
})

/**
 * Header-search request coordinator.
 *
 * The header search (retailer app-proxy path, the guest fallback and the
 * price-enrichment call) fires overlapping fetches as the user types. Without
 * a guard, whichever response resolves LAST wins and overwrites the DOM — even
 * if it belongs to an older/broader query. That produced the reported bug where
 * the correct product briefly shows and is then replaced by unrelated results.
 *
 * Every new search bumps `id` and aborts the previous request. Response
 * handlers check `isStale(myId)` before touching the DOM so only the latest
 * query renders. `newRequest()` returns the id + AbortSignal for a fresh search.
 */
const opSearchRequest = {
  id: 0,
  controller: null,
  newRequest() {
    this.id += 1;
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    return { id: this.id, signal: this.controller.signal };
  },
  isStale(id) {
    return id !== this.id;
  }
};

/**
 * Determines whether a formatted money string represents a zero amount
 * (e.g. "0,00 kr", "$0.00", "0"). Robust to currency symbols, thousands
 * separators and varying decimal marks.
 * @param {string|number|null|undefined} formattedPrice
 * @returns {boolean}
 */
function isZeroFormattedPrice(formattedPrice) {
  if (formattedPrice == null || formattedPrice === '') return false;
  if (typeof formattedPrice === 'number') return formattedPrice === 0;
  const digits = String(formattedPrice).replace(/\D/g, '');
  if (!digits) return false;
  return parseInt(digits, 10) === 0;
}

/**
 * Predictive product search fallback for guests/customers without a retailer.
 * Uses Shopify's native predictive search JSON endpoint and renders results
 * into the same "products" tab layout as the retailer flow.
 * @param {HTMLElement} predictiveSearch - The <predictive-search> element.
 * @param {string} searchTerm - The query typed by the user.
 * @param {number} requestId - Generation id from opSearchRequest.newRequest().
 * @param {AbortSignal} signal - Signal that aborts when a newer search starts.
 */
async function fetchStandardProductSearch(predictiveSearch, searchTerm, requestId, signal) {
  const productsPanel = predictiveSearch.querySelector('.predictive-search__tabpanel[data-type="products"]');
  const productsTab = predictiveSearch.querySelector('.tablist__tab[data-type="products"]');
  const tabbedContent = predictiveSearch.querySelector('tabbed-content');
  const noResultsEl = predictiveSearch.querySelector('.predictive-search__no-results');

  predictiveSearch.querySelectorAll('.tablist__tab').forEach(tab => {
    tab.setAttribute('aria-selected', 'false');
    if (tab !== productsTab) tab.classList.add('hidden');
  });
  predictiveSearch.querySelectorAll('.predictive-search__tabpanel').forEach(panel => {
    panel.setAttribute('hidden', 'true');
    if (panel !== productsPanel) panel.innerHTML = '';
  });

  predictiveSearch.setAttribute('loading', '');
  predictiveSearch.setAttribute('results', true);
  predictiveSearch.open();

  const limit = window.theme?.settings?.pSearchLimit || 10;
  let searchFields = 'title,product_type,variants.title,vendor';
  if (window.theme?.settings?.pSearchIncludeSkus) searchFields += ',variants.sku';
  if (window.theme?.settings?.pSearchIncludeTags) searchFields += ',tag';

  const searchParams = new URLSearchParams();
  searchParams.set('q', searchTerm);
  searchParams.set('resources[type]', 'product');
  searchParams.set('resources[limit]', String(limit));
  searchParams.set('resources[options][fields]', searchFields);

  try {
    const response = await fetch(`/search/suggest.json?${searchParams.toString()}`, { signal });
    if (!response.ok) throw new Error(response.status);
    const data = await response.json();
    // Ignore a response that has been superseded by a newer search.
    if (opSearchRequest.isStale(requestId)) return;
    const products = data?.resources?.results?.products || [];

    if (!products.length) throw new Error('No results found');

    const hideZeroPrice = !!window.theme?.settings?.hideZeroPrice;
    let productResults = '';
    const productHandles = [];
    const quickAddProducts = [];
    for (const product of products) {
      if (!product.handle) continue;
      productHandles.push(product.handle);
      const imageUrl = product.image || product.featured_image?.url || '';
      const sku = product.variants?.[0]?.sku || '';
      const priceIsZero = isZeroFormattedPrice(product.price);
      const showPrice = !!product.price && !(hideZeroPrice && priceIsZero);
      const variantId = product.variants?.[0]?.id;
      if (window.LocalCartSearch?.enabled() && variantId) {
        const quickAddProduct = {
          key: String(variantId),
          sku,
          handle: product.handle,
          title: product.title,
          image: imageUrl,
          priceHtml: showPrice ? product.price : ''
        };
        quickAddProducts.push(quickAddProduct);
        productResults += window.LocalCartSearch.renderRow(quickAddProduct);
        continue;
      }
      productResults += `<li class="predictive-search__item" role="option">
        <a class="predictive-result predictive-result--suggestion block focus-inset" href="/products/${product.handle}">
          ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="op-search-product-image">` : ''}
          <span class="op-search-product-title-price inline-flex flex-wrap flex-col gap-2">
            <span class="font-bold">${product.title}</span>
            ${showPrice ? `<span class="op-search-product-price"${sku ? ` data-sku="${sku}"` : ''}>${product.price}</span>` : ''}
          </span>
        </a>
      </li>`;
    }

    if (!productResults) throw new Error('No results found');

    productsPanel.innerHTML = quickAddProducts.length
      ? window.LocalCartSearch.wrapResults(productResults, quickAddProducts.length)
      : productResults;
    productsTab.classList.remove('hidden');
    productsTab.setAttribute('aria-selected', 'true');
    productsPanel.removeAttribute('hidden');

    tabbedContent.classList.remove('hidden');
    noResultsEl.classList.add('hidden');

    if (quickAddProducts.length) {
      window.LocalCartSearch.hydrate(productsPanel, { variantIds: quickAddProducts.map((product) => product.key) }, { signal, isStale: () => opSearchRequest.isStale(requestId) });
    } else if (productHandles.length) {
      fetchProductExtraData(productHandles, requestId, signal);
    }
  } catch (error) {
    // A request aborted by a newer search, or one that has been superseded,
    // must not render anything.
    if (error?.name === 'AbortError' || opSearchRequest.isStale(requestId)) return;
    console.error(error);
    tabbedContent.classList.add('hidden');
    let noResultsMessage = window.theme.strings.opSearch.noResults.replace('[keywords]', `"${searchTerm}"`);
    if (searchTerm.length < 3) {
      noResultsMessage = window.theme.strings.opSearch.noResultsNoKeywords;
    }
    noResultsEl.textContent = noResultsMessage;
    noResultsEl.classList.remove('hidden');
  } finally {
    if (!opSearchRequest.isStale(requestId)) {
      predictiveSearch.removeAttribute('loading');
      predictiveSearch.setAttribute('results', true);
      predictiveSearch.open();
    }
  }
}

/**
 * Enriches rendered search results with catalog/quantity-break prices.
 * @param {string[]} handles - Product handles to look up.
 * @param {number} [requestId] - Generation id; when provided, a stale response
 *   is ignored so it cannot overwrite the prices of a newer result set.
 * @param {AbortSignal} [signal] - Aborts when a newer search starts.
 */
async function fetchProductExtraData(handles, requestId, signal) {
  const hideZeroPrice = !!window.theme?.settings?.hideZeroPrice;
  const searchQuery = handles.join(' OR ');
  await fetch(`/search?q=${encodeURIComponent(searchQuery)}&type=product&view=product-details`, { signal })
    .then(response => response.json())
    .then(products => {
      // Ignore a response that has been superseded by a newer search.
      if (requestId !== undefined && opSearchRequest.isStale(requestId)) return;
      document.querySelectorAll('.op-search-product-price').forEach(price => {
        const sku = price.dataset.sku;
        if (sku) {
          const variant = products.find(product => product.variants.find(variant => variant.sku === sku));
          if (variant) {
            let vPrice = variant.price;
            if (variant.priceBreaks && variant.priceBreaks.length) {
              let smallestPrice = variant.priceBreaks[0].price;
              variant.priceBreaks.forEach(priceBreak => {
                if (priceBreak.price < smallestPrice) {
                  smallestPrice = priceBreak.price
                }
              });
              if (smallestPrice && smallestPrice < vPrice) {
                vPrice = smallestPrice;
              }
            }
            if (hideZeroPrice && Number(vPrice) === 0) {
              price.textContent = '';
              price.classList.add('hidden');
            } else {
              price.textContent = `${window.theme.strings.opSearch.fromPrice} ${Shopify.formatMoney(vPrice)}`;
            }
          }
        }
      })
    })
    .catch(error => {
      // Swallow aborts from a superseded search; surface anything else.
      if (error?.name === 'AbortError') return;
      console.error(error);
    });
}

Shopify.formatMoney = function(cents, format) {
  if (typeof cents == 'string') {
  cents = cents.replace('.', '');
  }
  var value = '';
  var placeholderRegex = /{{\s*(\w+)\s*}}/;
  var formatString = (format || this.money_format);
  function defaultOption(opt, def) {
      return (typeof opt == 'undefined' ? def : opt);
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) {
          return 0;
      }
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents = parts[1] ? (decimal + parts[1]) : '';
      return dollars + cents;
  }
  switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
      case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
      case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
      case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
  }
  return formatString.replace(placeholderRegex, value);
};

// stock status PDP
// define op-product-stock-status custom element, check if existed
if (!customElements.get('op-product-stock-status')) {
  class OpProductStockStatus extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      // console.log('op-product-stock-status connected');
      const variantSkus = document.getElementById('op-variant-skus').textContent;
      const variantSkusData = JSON.parse(variantSkus);
      if (variantSkusData && variantSkusData.variants && variantSkusData.variants.length) {
        let options = {
          retailerId: window.theme.customer.retailerId,
          products: [variantSkusData]
        }
        fetch(`/apps/indeks-retail-dashboard/proxy/order/stock`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(options)
        })
        .then(response => response.json())
        .then(data => {
          const productId = this.dataset.productId;
          if (productId && data[productId]) {
            let variantSku = this.dataset.variantSku;
            if (variantSku) {
              data[productId].inventory.forEach(inventoryItem => {
                if (inventoryItem.sku === variantSku) {
                  if (inventoryItem.dsvAvailableStock > 0) {
                    this.classList.remove('out-stock');
                  } else {
                    this.classList.add('out-stock');
                  }
                  if (this.querySelector('.pss__value--ir-stock')) {
                    this.querySelector('.pss__value--ir-stock').textContent = inventoryItem.dsvAvailableStock;
                    this.querySelector('.pss__value--ir-stock').classList.remove('loading');
                  }
                  if (this.querySelector('.pss__value--ir-physical-stock')) {
                    this.querySelector('.pss__value--ir-physical-stock').textContent = inventoryItem.dsvTotalStock;
                    this.querySelector('.pss__value--ir-physical-stock').classList.remove('loading');
                  }
                  if (this.querySelector('.pss__value--store-order')) {
                    this.querySelector('.pss__value--store-order').textContent = inventoryItem.incomingStock;
                    this.querySelector('.pss__value--store-order').classList.remove('loading');
                  }
                  if (this.querySelector('.pss__value--open-orders')) {
                    this.querySelector('.pss__value--open-orders').textContent = inventoryItem.avalableStock;
                    this.querySelector('.pss__value--open-orders').classList.remove('loading');
                  }
                  if (this.querySelector('.pss__value--store-available')) {
                    this.querySelector('.pss__value--store-available').textContent = inventoryItem.totalStock;
                    this.querySelector('.pss__value--store-available').classList.remove('loading');
                  }
                }
              })
            }
          }
        })
        .catch(error => {
          console.error(error);
          this.classList.add('hidden')
        });
      }
    }
  }
  customElements.define('op-product-stock-status', OpProductStockStatus);
}

if (!customElements.get('op-open-orders')) {
  class OpOpenOrders extends HTMLElement {
    connectedCallback() {
      if (!window.theme.customer.retailerId) return;

      const variantSkusEl = document.getElementById('op-variant-skus');
      if (!variantSkusEl) return;

      const variantSkusData = JSON.parse(variantSkusEl.textContent);
      if (!variantSkusData?.variants?.length) return;

      const options = {
        retailerId: window.theme.customer.retailerId,
        products: [variantSkusData]
      };

      fetch('/apps/indeks-retail-dashboard/proxy/order/reference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options)
      })
      .then(response => response.json())
      .then(data => {
        const productId = variantSkusData.id;
        if (productId && data[productId]?.orderReference) {
          this.renderOpenOrders(data[productId].orderReference);
        }
      })
      .catch(error => {
        console.error(error);
      });
    }

    renderOpenOrders(orderReference) {
      if (!orderReference?.length) {
        return;
      }

      const { regularQty, campaignQty } = getOpenOrderTotals(orderReference);
      if (regularQty === 0 && campaignQty === 0) {
        return;
      }

      const unique = [...new Map(orderReference.map(order => [order.orderId || order.axOrderName, order])).values()];
      const displayableOrders = unique.filter(order => order.axOrderName || order.orderId);
      if (!displayableOrders.length) {
        return;
      }

      const container = this.querySelector('.op-open-orders__list');
      if (container) {
        const strings = window.theme.strings.opOpenOrders;
        container.replaceChildren();

        for (const order of displayableOrders) {
          const orderIdentifier = order.axOrderName || order.orderId;
          const queryParameter = order.axOrderName ? 'axId' : 'id';
          const campaign = order.isCampaign ? ' (K)' : '';
          const link = document.createElement('a');
          link.href = `/pages/ordredetaljer?${new URLSearchParams({ [queryParameter]: orderIdentifier })}`;
          link.className = 'op-open-orders__link';
          link.textContent = `${orderIdentifier} - ${strings.quantity}: ${order.quantity}${campaign}`;
          container.appendChild(link);
        }
      }
      this.classList.remove('hidden');
    }
  }
  customElements.define('op-open-orders', OpOpenOrders);
}
